import React from 'react';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import Link from 'next/link';
import { twJoin } from 'tailwind-merge';
import { ContactElementTile } from './types/ContactElementTile';
import Container from '../../shared/Container';
import OptimizedImage from '../../shared/OptimizedImage';
import ButtonLink from '../../shared/ButtonLink';

export default function ContactElement({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: ContactElementTile;
  trackingPrefix?: string;
}>) {
  const contactElements = tile['contact-element'].chunks;
  return (
    <Container>
      <div
        className={twJoin(
          'flex flex-wrap',
          contactElements.length === 2
            ? 'justify-center gap-25 md:gap-37'
            : 'justify-around gap-10 md:gap-20',
        )}
      >
        {contactElements.map((chunk, index) => {
          const image = extractComponentFromChunk(chunk, 'image', 'ImageContent');
          const name = extractComponentFromChunk(chunk, 'name', 'SingleLineContent')?.text;
          const role = extractComponentFromChunk(chunk, 'role', 'SingleLineContent');
          const cta = extractComponentFromChunk(chunk, 'cta', 'PieceContent');
          const icon = extractComponentFromChunk(chunk, 'icon', 'ImageContent');
          const iconLink = extractComponentFromChunk(chunk, 'icon-link', 'SingleLineContent')?.text;

          return (
            <div key={index} className="flex flex-col items-center gap-1 md:gap-4">
              <OptimizedImage
                imageComponent={image}
                width={300}
                height={300}
                sizes="(max-width: 640pxpx) 100vw, 30vw"
                className="roudned-full aspect-square w-50 object-cover md:w-75"
              />
              <h3 className="text-2xl font-bold md:text-3xl">{name}</h3>
              {role && <p className="text-lg md:text-xl">{role.text}</p>}
              {cta && (
                <ButtonLink
                  className="mt-2 w-fit md:mt-4"
                  ctaComponent={cta}
                  trackingPrefix={trackingPrefix}
                />
              )}
              {icon && iconLink && (
                <Link
                  href={iconLink}
                  className="mt-2 md:mt-4"
                  aria-label={icon?.images?.[0]?.altText || name || ''}
                >
                  <OptimizedImage
                    sizes="50"
                    className="h-12 w-auto"
                    imageComponent={icon}
                    width={50}
                    height={50}
                  />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </Container>
  );
}
